import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { fmtGram, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hideMilyem = user?.hide_milyem;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex justify-center items-center bg-obsidian"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  if (!order) return <div className="min-h-screen flex justify-center items-center bg-obsidian text-muted-foreground">Sipariş bulunamadı.</div>;

  return (
    <div className="min-h-screen bg-neutral-200 py-6 px-4 print:bg-white print:p-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 16mm; }
          body { background: #fff; }
        }
      `}</style>

      <div className="no-print max-w-[800px] mx-auto flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-neutral-700 hover:text-black transition-colors">
          <ArrowLeft size={16} /> Geri
        </button>
        <button
          data-testid="print-invoice-button"
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 h-10 bg-black text-white rounded-md hover:bg-neutral-800 transition-colors"
        >
          <Printer size={16} /> Yazdır / PDF Kaydet
        </button>
      </div>

      <div data-testid="invoice-document" className="max-w-[800px] mx-auto bg-white text-neutral-900 shadow-lg print:shadow-none p-10">
        <div className="flex items-start justify-between border-b-2 border-neutral-900 pb-5">
          <div>
            <span className="inline-block bg-black rounded-lg px-5 py-3"><img src="/orse-logo-full.png" alt="ORSE GOLD" className="h-16 w-auto object-contain" /></span>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold">PROFORMA FATURA</div>
            <div className="text-sm text-neutral-600 mt-1 font-mono">{order.order_number}</div>
            <div className="text-xs text-neutral-500 mt-1">{fmtDate(order.created_at)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-neutral-400 mb-1">Müşteri</div>
            <div className="font-semibold">{order.company_name}</div>
            <div className="text-neutral-600">{order.contact_person}</div>
            <div className="text-neutral-600">{order.phone}</div>
            <div className="text-neutral-600">Müşteri Kodu: {order.customer_code}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-widest uppercase text-neutral-400 mb-1">Durum</div>
            <div className="font-semibold">{order.status}</div>
            <div className={`text-[11px] mt-1 font-medium ${order.gram_finalized ? "text-emerald-700" : "text-amber-700"}`}>{order.gram_finalized ? "Gramlar kesinleşmiştir" : "Gramlar tahminidir"}</div>
          </div>
        </div>

        <table className="w-full mt-8 text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-neutral-300 text-left text-neutral-500 text-xs uppercase">
              <th className="py-2">Ürün</th>
              <th className="py-2">Kod</th>
              <th className="py-2 text-center">Ayar</th>
              <th className="py-2 text-right">Adet</th>
              <th className="py-2 text-right">Gram</th>
              {!hideMilyem && <th className="py-2 text-right">Milyem</th>}
              <th className="py-2 text-right">Has Gr</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it, idx) => (
              <tr key={idx} className="border-b border-neutral-200">
                <td className="py-2.5">{it.name}</td>
                <td className="py-2.5 font-mono text-xs text-neutral-500">{it.code}</td>
                <td className="py-2.5 text-center">{it.karat}</td>
                <td className="py-2.5 text-right">{it.quantity}</td>
                <td className="py-2.5 text-right">{fmtGram(it.line_gram)}</td>
                {!hideMilyem && <td className="py-2.5 text-right">{it.milyem || 0}</td>}
                <td className="py-2.5 text-right">{fmtGram(it.line_has)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mt-8">
          <div className="w-full max-w-xs">
            <div className="text-[10px] tracking-widest uppercase text-neutral-400 mb-2">Ayar Bazında Dağılım</div>
            {order.karat_breakdown?.map((b) => (
              <div key={b.karat} className="flex justify-between py-1.5 border-b border-neutral-200 text-sm">
                <span className="text-neutral-600">{b.karat} Ayar</span>
                <span className="font-medium">{fmtGram(b.gram)} gr <span className="text-neutral-400">/ saf {fmtGram(b.has)} gr</span></span>
              </div>
            ))}
            <div className="flex justify-between py-1 text-sm mt-1">
              <span className="text-neutral-600">Saf Has Altın</span>
              <span className="font-medium">{fmtGram(order.total_pure_has)} gr</span>
            </div>
            <div className="flex justify-between py-1 text-sm">
              <span className="text-neutral-600">İşçilik (Has)</span>
              <span className="font-medium">{fmtGram(order.total_isc_has)} gr</span>
            </div>
            <div className="flex justify-between py-2 mt-1 border-t-2 border-neutral-900">
              <span className="font-semibold">Toplam Has Altın</span>
              <span className="font-bold" style={{ color: "#8a6d1a" }}>{fmtGram(order.total_has)} gr</span>
            </div>
          </div>
        </div>

        {order.note && (
          <div className="mt-8 text-sm">
            <div className="text-[10px] tracking-widest uppercase text-neutral-400 mb-1">Müşteri Notu</div>
            <p className="text-neutral-700">{order.note}</p>
          </div>
        )}

        <div className="mt-10 pt-5 border-t border-neutral-200 text-[11px] text-neutral-400 text-center">
          Bu belge bir proforma faturadır ve resmi bir mali belge değildir. Tutarlar has altın (gram) cinsindendir.
          {order.gram_finalized
            ? " Ürünler üretilmiş olup gramlar ve has altın kesinleşmiştir."
            : " Bu bir siparişe-üretim siparişidir; gramlar tahminidir, kesin has altın üretim sonrası belirlenir."}
        </div>
      </div>
    </div>
  );
}
